import { VELOCITY_INPUTS, computePlan, type FunnelPlan } from "./velocity-targets";
// Per-funnel target system on the monthly revenue goal (Joey Forslund, Oct 19, 2026, 8:52 PM ET): Jeremy Haynes AI's
// split of the $200K/month plan into the Book a call and Webinar funnels, from the velocity-metrics PDF
// (blended, Sep 1–Oct 19, 2026). One source for the target panels on Velocity Metrics, the Book a call funnel tab
// and the Webinars tab, and for the per-funnel volume rows on the Velocity / Book a call sheets.
//
// How it scales
// - (Cr8Her copy) The monthly goal is ONE shared server-side value; on UU it is the KPI targets period "velocity_cash_monthly".
// - Volume targets scale linearly: target = value at $200K × (goal ÷ $200,000). At $200K every number below is
//   exactly the number in Jeremy's message.
// - Blended weekly volumes are the whole offer (revenue $46K ≈ $200K ÷ 4.33 weeks, cash = 50% of revenue). Each funnel
//   gets a fixed share of the blended number (FUNNEL_SPLIT, from the message), and Webinar = Blended − Book a call,
//   so the two funnels always add back to Blended at any goal.
// - Top-of-funnel volumes (page views, opt-ins, applications, qualified leads, outbound bookings) are funnel-only
//   numbers from the message; Blended applications / qualified = the two funnels added.
// - A period of N days is scored at weekly × (N ÷ 7 whole weeks) + daily × (leftover days): a full Mon–Sun week uses
//   Jeremy's weekly number, any other day his daily number (the rule the Sheets views already use). A view made of
//   several columns adds the columns' targets.
// - Rate targets never scale with the goal (FUNNEL_RATE_TARGETS); judge them on 7+ days of data.
//
// VELOCITY METRICS = A CASH COLLECTED GOAL (Joey, Oct 19, 2026, 9:47–9:48 PM ET: "The target should be based on cash
// collected: $200,000 cash collected not revenue" / "Cash collected is the only target, so don't even make a revenue
// target"). The Velocity Metrics page (goal panel, list sheet, cards) uses the cash* functions at the bottom of this
// file, never volumePlan directly: cash = goal ÷ 4.33 a week (÷ 7 a day), NO revenue target, and every volume is
// Jeremy's number × (cash goal ÷ $100K), because his plan was $200K revenue ≈ $100K cash (so they double at $200K).
// The Book a call tab still reads volumePlan / sheetVolumeTarget unchanged (Joey, 9:53 PM: leave that tab as it was).

export const GOAL_BASE_MONTHLY_REVENUE = 200_000;
export const DEFAULT_MONTHLY_GOAL = 200_000;
/** Jeremy's month → week factor: $200K ÷ 4.33 ≈ $46K a week. */
export const WEEKS_PER_MONTH = 4.33;
export const CASH_SHARE_OF_REVENUE = 0.5;

export type TargetFunnel = "blended" | "bookCall" | "webinar";
export type VolumeKey = "pageViews" | "optins" | "apps" | "qualified" | "booked" | "outboundBooked" | "showed" | "closed" | "revenue" | "cash";
export type VolumePlan = { weekly: number; daily: number };

/** Blended (whole offer) weekly / daily at the $200K goal, from the message. */
export const BLENDED_AT_BASE: Record<"booked" | "showed" | "closed" | "revenue" | "cash", VolumePlan> = {
  booked: { weekly: 37, daily: 5.3 },       // 21 + 16 a week; 3 + 2.3 a day
  showed: { weekly: 26, daily: 4 },         // 13 + 13; 2 + 2
  closed: { weekly: 8, daily: 8 / 7 },      // 3.5 + 4.5
  revenue: { weekly: 46_000, daily: 6_600 },// $21K + $25K; $3,000 + $3,600 a day
  cash: { weekly: 23_000, daily: 3_280 },   // $10K + $13K; $1,430 + $1,850 a day
};

/**
 * Book a call's share of each blended volume (Webinar gets the rest). From the message: booked closing calls split
 * 21 / 16 (≈57–58% Book a call), showed 13 / 13, closed 3.5 / 4.5, revenue $21K / $25K (45.7%), cash $10K / $13K.
 * Daily shares are the message's daily split (3 of 5.3 booked, $3,000 of $6,600, $1,430 of $3,280 …).
 */
export const FUNNEL_SPLIT: Record<keyof typeof BLENDED_AT_BASE, { weekly: number; daily: number }> = {
  booked: { weekly: 21 / 37, daily: 3 / 5.3 },
  showed: { weekly: 13 / 26, daily: 2 / 4 },
  closed: { weekly: 3.5 / 8, daily: 3.5 / 8 },
  revenue: { weekly: 21_000 / 46_000, daily: 3_000 / 6_600 },
  cash: { weekly: 10_000 / 23_000, daily: 1_430 / 3_280 },
};

/** Funnel-only volumes at $200K (Blended = the two added where both funnels have the metric). */
export const FUNNEL_ONLY_AT_BASE: Record<"bookCall" | "webinar", Partial<Record<"pageViews" | "optins" | "apps" | "qualified" | "outboundBooked", VolumePlan>>> = {
  bookCall: {
    pageViews: { weekly: 590, daily: 85 },
    apps: { weekly: 77, daily: 11 },
    qualified: { weekly: 32, daily: 5 },
    outboundBooked: { weekly: 7, daily: 1 },   // "at least 7 of the weekly 21 from outbound, so one outbound booking a day"
  },
  webinar: {
    optins: { weekly: 215, daily: 31 },
    apps: { weekly: 34, daily: 5 },
    qualified: { weekly: 15, daily: 2 },
    outboundBooked: { weekly: 5, daily: 5 / 7 },
  },
};

export const VOLUME_LABEL: Record<VolumeKey, string> = {
  pageViews: "Page views", optins: "Opt-ins", apps: "Applications", qualified: "Qualified leads",
  booked: "Booked closing calls", outboundBooked: "…of which outbound", showed: "Showed calls", closed: "Closed deals",
  revenue: "Revenue generated", cash: "Cash collected",
};
export const MONEY_VOLUMES = new Set<VolumeKey>(["revenue", "cash"]);
export const VOLUME_ORDER: Record<TargetFunnel, VolumeKey[]> = {
  blended: ["apps", "qualified", "booked", "outboundBooked", "showed", "closed", "revenue", "cash"],
  bookCall: ["pageViews", "apps", "qualified", "booked", "outboundBooked", "showed", "closed", "revenue", "cash"],
  webinar: ["optins", "apps", "qualified", "booked", "outboundBooked", "showed", "closed", "revenue", "cash"],
};

export function goalScale(monthlyGoal: number) {
  return Number.isFinite(monthlyGoal) && monthlyGoal > 0 ? monthlyGoal / GOAL_BASE_MONTHLY_REVENUE : 1;
}

/** Weekly / daily plan for one funnel and metric at the given monthly goal (null = no target for that funnel). */
export function volumePlan(funnel: TargetFunnel, key: VolumeKey, monthlyGoal: number): VolumePlan | null {
  const scale = goalScale(monthlyGoal);
  const scaled = (plan: VolumePlan): VolumePlan => ({ weekly: plan.weekly * scale, daily: plan.daily * scale });
  if (key in BLENDED_AT_BASE) {
    const blended = BLENDED_AT_BASE[key as keyof typeof BLENDED_AT_BASE];
    if (funnel === "blended") return scaled(blended);
    const split = FUNNEL_SPLIT[key as keyof typeof FUNNEL_SPLIT];
    const bookCall = { weekly: blended.weekly * split.weekly, daily: blended.daily * split.daily };
    return scaled(funnel === "bookCall" ? bookCall : { weekly: blended.weekly - bookCall.weekly, daily: blended.daily - bookCall.daily });
  }
  const only = key as keyof (typeof FUNNEL_ONLY_AT_BASE)["bookCall"];
  if (funnel !== "blended") { const plan = FUNNEL_ONLY_AT_BASE[funnel][only]; return plan ? scaled(plan) : null; }
  const bookCall = FUNNEL_ONLY_AT_BASE.bookCall[only]; const webinar = FUNNEL_ONLY_AT_BASE.webinar[only];
  if (!bookCall || !webinar) return null; // page views / opt-ins belong to one funnel only
  return scaled({ weekly: bookCall.weekly + webinar.weekly, daily: bookCall.daily + webinar.daily });
}

/** Target for a period of `days` days: whole weeks at the weekly number, leftover days at the daily number. */
export function periodTarget(plan: VolumePlan, days: number) {
  const whole = Math.max(0, Math.floor(days / 7)); const rest = Math.max(0, days - whole * 7);
  return plan.weekly * whole + plan.daily * rest;
}

export function volumeTargetFor(funnel: TargetFunnel, key: VolumeKey, monthlyGoal: number, columnDays: number[]) {
  const plan = volumePlan(funnel, key, monthlyGoal);
  if (!plan) return null;
  return columnDays.reduce((sum, days) => sum + periodTarget(plan, days), 0);
}

// ─── The older sheet / card views (Velocity Metrics list + cards, Book a call sheet) ─────────────────────────────
// Their rows are named by metric label; these map onto the goal volumes above so every view scores against the same
// goal-derived numbers (Oct 19, 2026). "Booked Calls" / "(IB)" = inbound = all booked closing calls − outbound.
export type SheetVolume = VolumeKey | "inboundBooked";
export const SHEET_LABEL_VOLUME: Record<string, SheetVolume> = {
  "Page Views": "pageViews", "Opt-ins": "optins", "Applications": "apps", "Leads": "apps",
  "Overall Qualified Leads": "qualified",
  "Total Booked Closing Calls (All)": "booked",
  "Booked Calls": "inboundBooked", "Total Booked Closing Calls (IB)": "inboundBooked",
  "Book Calls Outbound": "outboundBooked", "Total Booked Closing Calls (OB)": "outboundBooked",
  "Showed Calls": "showed", "Total Closing Calls Completed": "showed",
  "Closed Deals": "closed", "Deals Closed": "closed",
  "Revenue Generated": "revenue", "Cash Collected": "cash",
};

/** Weekly / daily goal plan for a sheet row label (null = the goal plan has no such volume for that funnel). */
export function sheetVolumePlan(funnel: TargetFunnel, label: string, monthlyGoal: number): VolumePlan | null {
  const key = SHEET_LABEL_VOLUME[label];
  if (!key) return null;
  if (key !== "inboundBooked") return volumePlan(funnel, key, monthlyGoal);
  const all = volumePlan(funnel, "booked", monthlyGoal); const outbound = volumePlan(funnel, "outboundBooked", monthlyGoal);
  return all && outbound ? { weekly: all.weekly - outbound.weekly, daily: all.daily - outbound.daily } : null;
}

/** Goal-derived volume target for a sheet row over columns of these day counts (same rule as the goal panel). */
export function sheetVolumeTarget(funnel: TargetFunnel, label: string, monthlyGoal: number, columnDays: number[]) {
  const plan = sheetVolumePlan(funnel, label, monthlyGoal);
  return plan ? columnDays.reduce((sum, days) => sum + periodTarget(plan, days), 0) : null;
}

// ─── Rates: fixed, never scale with the goal ────────────────────────────────────────────────────────────────

export type RateKind = "pct" | "money";
export type RateTarget = {
  id: string; funnel: TargetFunnel; label: string; kind: RateKind;
  /** min = at least `min`; max = at most `max`; range = lo–hi, where above the range still counts as on track. */
  direction: "min" | "max" | "range"; min?: number; max?: number;
  display: string; note?: string; highlight?: boolean;
};

/**
 * Average sale price targets, per funnel = each funnel's REAL average sale price, not the $6,000 offer price
 * (Joey Forslund, Oct 20, 2026, 5:19 PM ET: "that should be our target for the velocity metrics around the board ...
 * individually for the book call funnel and the webinar funnel"). Hard-set Oct 20, 2026 from the dashboard's own data,
 * calls dated Sep 1 – Oct 20, 2026 (Eastern), funnel = the dashboard partition (Book a call EOC ids from
 * getBookedCallFunnelEocIds, Webinar = getDashboardWebinarEocIds; newest-UTM attribution):
 *   ASP = contracted value (EOC revenueGenerated, not cash) ÷ unique closed deals (one per buyer: their latest
 *   Closed/Downsell EOC; repeat EOC rows for the same deal counted once; deposit-only buyers not yet closed left out;
 *   fully refunded deals left out: Darcy W. $5,500 and Atonnia G. $2,000 of $2,000, both Book a call).
 *   Book a call: 15 deals, $89,000  → $5,933.  Webinar: 24 deals, $125,500 → $5,229 (3 × $500 downsells included).
 *   Blended: 39 deals, $214,500 → $5,500.
 * Refresh these from the same data when they drift; the Velocity "Average Sale Price" row itself is revenue ÷ closed
 * EOC rows (deposits and repeat rows included), so it reads lower than these deal-level numbers.
 */
export const VELOCITY_ASP_BY_FUNNEL: Record<TargetFunnel, number> = { bookCall: VELOCITY_INPUTS.bookCall.asp, webinar: VELOCITY_INPUTS.webinar.asp, blended: VELOCITY_INPUTS.blended.asp }; // UU cash per close (config/velocity-targets.ts)

export const FUNNEL_RATE_TARGETS: RateTarget[] = [
  { id: "blended.cancellationRate", funnel: "blended", label: "Cancellation rate", kind: "pct", direction: "max", max: 20, display: "under 20%", highlight: true, note: "The single biggest failure on the sheet: weeks ran 24–73% vs under 20%. Every cancellation is a booked call already paid for. % of calls booked (active + cancelled), by booking status." },
  { id: "bookCall.applicationRate", funnel: "bookCall", label: "Application rate", kind: "pct", direction: "min", min: 13, display: "13%+", note: "Applications ÷ page views. Hold it; steady there." },
  { id: "bookCall.showRate", funnel: "bookCall", label: "Show rate", kind: "pct", direction: "min", min: 70, display: "70%", note: "This funnel's whole job: all-time 58% vs 70%." , highlight: true },
  { id: "bookCall.closeRate", funnel: "bookCall", label: "Close rate", kind: "pct", direction: "min", min: 30, display: "30%+", note: "Closed ÷ showed. All-time 37% is fine." },
  { id: "bookCall.averageSalePrice", funnel: "bookCall", label: "Average sale price", kind: "money", direction: "min", display: "monthly avg", note: "Cash collected ÷ closes (deposits are not closes), same as the Overview's Avg. Cash / Close. No fixed target: scored against this funnel's actual monthly average (Joey, Oct 20, 2026)." },
  { id: "bookCall.cancellationRate", funnel: "bookCall", label: "Cancellation rate", kind: "pct", direction: "max", max: 20, display: "under 20%", note: "This funnel's cancellations, % of calls booked (by booking status)." },
  { id: "webinar.optinRate", funnel: "webinar", label: "Opt-in rate", kind: "pct", direction: "min", min: 20, display: "20%+", note: "Landing page opt-ins ÷ visitors (Webinars tab)." },
  { id: "webinar.attendanceShowRate", funnel: "webinar", label: "Webinar attendance show rate", kind: "pct", direction: "min", min: 30, display: "30%+", note: "Attended ÷ registrants. Hold it." },
  { id: "webinar.atPitch", funnel: "webinar", label: "At pitch", kind: "pct", direction: "min", min: 70, display: "70%+", note: "Attendees still on at the pitch minute." },
  { id: "webinar.bookingRate", funnel: "webinar", label: "Booking rate", kind: "pct", direction: "range", min: 20, max: 30, display: "20–30%", note: "Bookings ÷ attendees at pitch." },
  { id: "webinar.totalAppRate", funnel: "webinar", label: "Total app rate", kind: "pct", direction: "range", min: 40, max: 60, display: "40–60%", note: "Applications ÷ attendees at pitch." },
  { id: "webinar.fullAppRate", funnel: "webinar", label: "Full app rate", kind: "pct", direction: "range", min: 30, max: 45, display: "30–45%", note: "Completed applications ÷ attendees at pitch." },
  { id: "webinar.qualAppRate", funnel: "webinar", label: "Qualified app rate", kind: "pct", direction: "range", min: 15, max: 25, display: "15–25%", note: "The real fix: qualified applications ÷ attendees at pitch.", highlight: true },
  { id: "webinar.optinToQualified", funnel: "webinar", label: "Opt-in → qualified app", kind: "pct", direction: "range", min: 6, max: 12, display: "6–12%", note: "Benchmark; all-time 0–2.5%. Biggest rate gap on the sheet." },
  { id: "webinar.addToCalendar", funnel: "webinar", label: "Add to calendar", kind: "pct", direction: "min", min: 25, display: "25%+", note: "Add-to-calendar clicks ÷ opt-ins." },
  { id: "webinar.whatsApp", funnel: "webinar", label: "WhatsApp joins", kind: "pct", direction: "min", min: 30, display: "30%+", note: "WhatsApp joins ÷ opt-ins." },
  { id: "webinar.dayOfAttendance", funnel: "webinar", label: "Day-of attendance", kind: "pct", direction: "range", min: 22, max: 35, display: "22–35%", note: "Show rate of people who registered on webinar day (Zoom)." },
  { id: "webinar.salesShowRate", funnel: "webinar", label: "Sales call show rate", kind: "pct", direction: "range", min: 60, max: 70, display: "60–70% (aim 70%)", note: "Calls taken ÷ calls due (Webinars tab)." },
  { id: "webinar.closeRate", funnel: "webinar", label: "Close rate", kind: "pct", direction: "range", min: 25, max: 35, display: "25–35%", note: "Don't touch the closers." },
  { id: "webinar.aov", funnel: "webinar", label: "AOV", kind: "money", direction: "min", display: "monthly avg", note: "Average order value (Webinars tab) = average sale price = cash per close. No fixed target: scored against the webinar funnel's actual monthly average (Joey, Oct 20, 2026)." },
  { id: "webinar.cashPerCall", funnel: "webinar", label: "Cash per call", kind: "money", direction: "min", min: 1200, display: "$1,200+", note: "Cash ÷ calls taken (Webinars tab)." },
  { id: "webinar.cancellationRate", funnel: "webinar", label: "Cancellation rate", kind: "pct", direction: "max", max: 20, display: "under 20%", note: "This funnel's cancellations, % of calls booked (by booking status)." },
];

/** A rate is "low sample" under a week of data or with fewer than this many records under it. */
export const LOW_SAMPLE_DAYS = 7;
export const LOW_SAMPLE_COUNT = 10;

// ─── Velocity Metrics: the monthly goal is CASH COLLECTED — URBAN UNITY numbers ────────────────────────────────────
// This page's layout, rows and scoring are copied from the Cr8Her dashboard (Joey Forslund, Oct 29, 2026: "the exact same,
// literally to a T ... and then just obviously have our Urban Unity data populate there"). The NUMBERS are Urban Unity's
// own model (config/velocity-targets.ts, Jeremy Haynes' AI $200K/month model for UU, Oct 27, 2026):
//   cash/week          = goal ÷ 4.33 ($46,189 at $200K); a day = the week ÷ 7
//   new cash           = 79% of cash (6-mo new vs MRR split; closes only print new cash)
//   closed deals/week  = new cash ÷ that funnel's ASP (cash per close: Blended $2,157, Webinar $1,861, Book a call $3,144)
//   calls taken        = closes ÷ close rate; booked = taken ÷ booked-to-taken (Blended) or show rate (funnels); apps = booked ÷ booking rate
//   funnel split       = last-touch cash mix: Webinar 31%, Book a call 4% (Blended = the whole offer, every funnel)
//   revenue/week       = new cash ÷ 60% cash-vs-revenue; net cash = cash × 95%
// Every volume scales linearly with the goal. Rates never scale.
/** The default monthly CASH COLLECTED goal (UU: $200,000, Jeremy's model). */
export const MONTHLY_CASH_GOAL = 200_000;
export const PLAN_MONTHLY_CASH = 100_000;
/** Cash collected ÷ revenue generated target (UU model: 60%). */
export const CASH_VS_REVENUE_TARGET = VELOCITY_INPUTS.blended.cashVsRev;
/** Refunds allowed, share of cash ("Refunds under 5% of cash"); net cash = cash minus this allowance. */
export const REFUND_ALLOWANCE = VELOCITY_INPUTS.blended.refundCap;
/** UU cash-per-close targets by funnel (VELOCITY_INPUTS asp). */
export const UU_ASP_BY_FUNNEL: Record<TargetFunnel, number> = { blended: VELOCITY_INPUTS.blended.asp, webinar: VELOCITY_INPUTS.webinar.asp, bookCall: VELOCITY_INPUTS.bookCall.asp };
export const velocityAsp = (funnel: TargetFunnel) => UU_ASP_BY_FUNNEL[funnel] ?? UU_ASP_BY_FUNNEL.blended;
export const VELOCITY_ASP = UU_ASP_BY_FUNNEL.blended;
/** Cash collected per closed deal = ASP ÷ new-cash share (closes print new cash; cash also carries MRR). */
export const cashPerClosedDeal = (funnel: TargetFunnel) => velocityAsp(funnel) / VELOCITY_INPUTS.newCashShare;
export const CASH_PER_CLOSED_DEAL = cashPerClosedDeal("blended");
/** UU funnel shares of the cash goal (last-touch cash mix). */
export const FUNNEL_CASH_SHARE: Record<TargetFunnel, number> = { blended: 1, webinar: VELOCITY_INPUTS.cashMix.webinar, bookCall: VELOCITY_INPUTS.cashMix.bookCall };
export const BOOK_CALL_CASH_SHARE = FUNNEL_CASH_SHARE.bookCall;
export const FULL_APP_SHARE = VELOCITY_INPUTS.blended.fullShare;
export const PARTIAL_APP_CAP_SHARE = 1 - VELOCITY_INPUTS.blended.fullShare;
/** Webinar cash by source (UU model benchmark: booked calls 60%, applications 20%, opt-ins 20%). */
export const WEBINAR_CASH_SOURCE_SHARE = { bookedCalls: 0.6, applications: 0.2, optins: 0.2 } as const;
export const WEBINAR_DEPOSIT_SHARE = VELOCITY_INPUTS.webinar.depositShare;
/** "41% share" style labels for titles. */
export const funnelSharePct = (funnel: TargetFunnel) => `${Math.round(FUNNEL_CASH_SHARE[funnel] * 100)}%`;

const cashGoalOf = (monthlyCashGoal: number) => Number.isFinite(monthlyCashGoal) && monthlyCashGoal > 0 ? monthlyCashGoal : MONTHLY_CASH_GOAL;
const round100 = (value: number) => Math.round(value / 100) * 100;
const weekPlan = (weekly: number): VolumePlan => ({ weekly, daily: weekly / 7 });

export function cashPlanFor(monthlyCashGoal: number): VolumePlan {
  return weekPlan(cashGoalOf(monthlyCashGoal) / WEEKS_PER_MONTH);
}
export const cashGoalAsPlanGoal = (monthlyCashGoal: number) => cashGoalOf(monthlyCashGoal) * (GOAL_BASE_MONTHLY_REVENUE / PLAN_MONTHLY_CASH);

/** Weekly cash for one funnel (Blended = goal ÷ 4.33; Webinar 31%, Book a call 4% of it). */
export function velocityCashWeekly(funnel: TargetFunnel, monthlyCashGoal: number) {
  return cashGoalOf(monthlyCashGoal) / WEEKS_PER_MONTH * FUNNEL_CASH_SHARE[funnel];
}

export type VelocityVolumeKey = VolumeKey | "landingVisitors" | "fullApps" | "partialApps" | "eocs" | "vslPlays" | "netCash";
const planCache = new Map<number, ReturnType<typeof computePlan>>();
function uuPlan(monthlyCashGoal: number) {
  const goal = cashGoalOf(monthlyCashGoal);
  let plan = planCache.get(goal);
  if (!plan) { plan = computePlan("jeremy", { ...VELOCITY_INPUTS, weeklyGoal: goal / WEEKS_PER_MONTH, monthlyGoal: goal }); planCache.set(goal, plan); }
  return plan;
}

/** Weekly target at a monthly CASH goal (null = no target for that funnel), from UU's model chain. */
export function velocityWeekly(funnel: TargetFunnel, key: VelocityVolumeKey, monthlyCashGoal: number): number | null {
  const plan = uuPlan(monthlyCashGoal);
  const p: FunnelPlan = plan[funnel];
  const I = VELOCITY_INPUTS;
  switch (key) {
    case "cash": return velocityCashWeekly(funnel, monthlyCashGoal);
    case "revenue": return round100(p.revenue);
    case "netCash": return round100(p.cash * (1 - REFUND_ALLOWANCE));
    case "closed": return Math.round(p.closes * 10) / 10;
    case "apps": return Math.round(p.apps * 10) / 10;
    case "fullApps": return Math.round(p.full);
    case "partialApps": return Math.round(p.partial);
    case "qualified": return Math.round(p.full * (funnel === "webinar" ? I.webinar.qualifiedOfFull : funnel === "bookCall" ? I.bookCall.qualifiedOfFull : I.blended.qualifiedOfFull) * 10) / 10;
    case "booked": return Math.round(p.booked * 10) / 10;
    // Book a call = inbound self-bookings by definition (no outbound); Blended / Webinar: 87% of bookings are setter-booked.
    case "outboundBooked": return funnel === "bookCall" ? null : Math.round(p.booked * (1 - (funnel === "webinar" ? I.webinar.inboundMix : I.blended.inboundMix)) * 10) / 10;
    case "showed": return Math.round(p.taken * 10) / 10;
    case "eocs": return funnel === "webinar" ? Math.round(12 * p.k * 10) / 10 : Math.round(p.taken / I.blended.eocCoverage * 10) / 10;
    case "optins": return funnel === "bookCall" ? null : Math.round(p.optins);
    case "pageViews": return funnel === "bookCall" ? Math.round(p.pageViews) : null;
    case "vslPlays": return funnel === "bookCall" ? Math.round(p.pageViews * I.bookCall.vslPerView) : null;
    case "landingVisitors": return funnel === "webinar" ? Math.round(p.optins / I.webinar.optinRate) : null;
  }
  return null;
}

/** Velocity's weekly / daily plan at a monthly CASH goal (daily = weekly ÷ 7). */
export function cashVolumePlan(funnel: TargetFunnel, key: VolumeKey, monthlyCashGoal: number): VolumePlan | null {
  const weekly = velocityWeekly(funnel, key, monthlyCashGoal);
  return weekly === null ? null : weekPlan(weekly);
}
export function cashVolumeTargetFor(funnel: TargetFunnel, key: VolumeKey, monthlyCashGoal: number, columnDays: number[]) {
  const plan = cashVolumePlan(funnel, key, monthlyCashGoal);
  return plan ? columnDays.reduce((sum, days) => sum + periodTarget(plan, days), 0) : null;
}
/** Velocity sheet row label → volume key on the Velocity plan. Partial Applications is a cap (see VELOCITY_CAP_LABELS). */
export const VELOCITY_LABEL_VOLUME: Record<string, VelocityVolumeKey | "inboundBooked"> = {
  ...SHEET_LABEL_VOLUME,
  "Full Applications": "fullApps", "Partial Applications": "partialApps", "EOCs": "eocs", "VSL Plays": "vslPlays", "Net Cash": "netCash",
};
/** Rows whose weekly number is a ceiling ("under X"), not a goal to hit. */
export const VELOCITY_CAP_LABELS = new Set(["Partial Applications"]);
/** Velocity sheet row label → plan at a monthly CASH goal (null = no target). */
export function cashSheetVolumePlan(funnel: TargetFunnel, label: string, monthlyCashGoal: number): VolumePlan | null {
  const key = VELOCITY_LABEL_VOLUME[label];
  if (!key) return null;
  if (key !== "inboundBooked") { const weekly = velocityWeekly(funnel, key, monthlyCashGoal); return weekly === null ? null : weekPlan(weekly); }
  const all = velocityWeekly(funnel, "booked", monthlyCashGoal); const outbound = velocityWeekly(funnel, "outboundBooked", monthlyCashGoal);
  return all !== null && outbound !== null ? weekPlan(all - outbound) : null;
}
export function cashSheetVolumeTarget(funnel: TargetFunnel, label: string, monthlyCashGoal: number, columnDays: number[]) {
  const plan = cashSheetVolumePlan(funnel, label, monthlyCashGoal);
  return plan ? columnDays.reduce((sum, days) => sum + periodTarget(plan, days), 0) : null;
}
